import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { LANG_LABEL } from "../lib/categories";
import { ExternalLink, BookOpen, Search as SearchIcon, X } from "lucide-react";

const CATEGORY_STYLES = {
  "Recherche scientifique":   { bg: "#1A1A18", fg: "#C4944A", accent: "#C4944A" },
  "Cannabis médical":         { bg: "#4A7A3D", fg: "#FAF8F3", accent: "#FAF8F3" },
  "Politiques publiques":     { bg: "#C4944A", fg: "#1A1A18", accent: "#1A1A18" },
  "Médias spécialisés":       { bg: "#1A1A18", fg: "#FAF8F3", accent: "#8FAF82" },
  "Maurice & océan Indien":   { bg: "#8FAF82", fg: "#1A1A18", accent: "#4A7A3D" },
  "Réduction des risques":    { bg: "#FAF8F3", fg: "#1A1A18", accent: "#4A7A3D" },
  "Histoire & culture":       { bg: "#4A7A3D", fg: "#FAF8F3", accent: "#C4944A" },
};
const DEFAULT_STYLE = { bg: "#1A1A18", fg: "#FAF8F3", accent: "#C4944A" };

export default function Ressources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ language: "", q: "", category: "" });

  useEffect(() => {
    api.get("/resources").then((r) => setResources(r.data)).finally(() => setLoading(false));
  }, []);

  const allCategories = useMemo(() => {
    const set = new Set(resources.map((r) => r.category));
    return Array.from(set);
  }, [resources]);

  const filtered = useMemo(() => {
    return resources.filter((r) => {
      if (filter.language && r.language !== filter.language) return false;
      if (filter.category && r.category !== filter.category) return false;
      if (filter.q) {
        const q = filter.q.toLowerCase();
        return (
          r.title.toLowerCase().includes(q) ||
          (r.description || "").toLowerCase().includes(q) ||
          (r.source_name || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [resources, filter]);

  const byCategory = useMemo(() => {
    const map = {};
    filtered.forEach((r) => { (map[r.category] = map[r.category] || []).push(r); });
    return map;
  }, [filtered]);
  const visibleCategories = Object.keys(byCategory);

  return (
    <div data-testid="ressources-page">
      {/* HERO */}
      <section className="border-b-2 border-[#1A1A18] bg-[#1A1A18] text-[#FAF8F3] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(196,148,74,0.6) 1.5px, transparent 1.5px)", backgroundSize: "32px 32px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 grid md:grid-cols-12 gap-10 items-end relative">
          <div className="md:col-span-9">
            <div className="font-mono-label text-[#C4944A] mb-4 flex items-center gap-2"><BookOpen size={14} /> Bibliothèque vivante</div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl leading-[0.92] uppercase">
              Aller<br /><span className="text-[#C4944A]">plus loin.</span>
            </h1>
            <p className="font-quote text-2xl md:text-3xl text-[#FAF8F3]/80 mt-8 max-w-3xl leading-snug">
              « {resources.length} sources fiables triées sur le volet : recherche, politiques, médias, histoire. »
            </p>
          </div>
          <div className="md:col-span-3 grid grid-cols-3 md:grid-cols-1 gap-3 md:text-right">
            <div className="border-2 border-[#C4944A] p-4">
              <div className="font-display text-4xl md:text-5xl text-[#C4944A] leading-none">{resources.length}</div>
              <div className="font-mono-label text-[#FAF8F3]/70 mt-2">RESSOURCES</div>
            </div>
            <div className="border-2 border-[#FAF8F3]/30 p-4">
              <div className="font-display text-4xl md:text-5xl text-[#FAF8F3] leading-none">{allCategories.length}</div>
              <div className="font-mono-label text-[#FAF8F3]/70 mt-2">CATÉGORIES</div>
            </div>
            <div className="border-2 border-[#FAF8F3]/30 p-4">
              <div className="font-display text-4xl md:text-5xl text-[#FAF8F3] leading-none">3</div>
              <div className="font-mono-label text-[#FAF8F3]/70 mt-2">LANGUES</div>
            </div>
          </div>
        </div>
      </section>

      {/* TOOLBAR — sticky chips */}
      <section className="border-b-2 border-[#1A1A18] bg-[#FAF8F3] sticky top-20 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-3">
          <div className="flex items-center gap-3 border-b-2 border-[#1A1A18] pb-2">
            <SearchIcon size={18} />
            <input
              type="search"
              value={filter.q}
              onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              placeholder="Rechercher dans les ressources…"
              className="bg-transparent flex-1 py-1 outline-none text-base font-display placeholder:text-[#4B4B48]/60"
              data-testid="ressources-search"
            />
            {(filter.q || filter.category || filter.language) && (
              <button
                onClick={() => setFilter({ language: "", q: "", category: "" })}
                className="font-mono-label text-[#4A7A3D] hover:text-[#1A1A18] flex items-center gap-1"
                data-testid="ressources-reset"
              >
                <X size={12} /> Réinitialiser
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: "thin" }}>
            <button
              onClick={() => setFilter({ ...filter, category: "" })}
              className={`font-mono-label px-3 py-1.5 border-2 whitespace-nowrap ${filter.category === "" ? "bg-[#1A1A18] text-[#FAF8F3] border-[#1A1A18]" : "border-[#E0D8CE] hover:border-[#1A1A18]"}`}
              data-testid="ressources-cat-all"
            >
              Tout · {resources.length}
            </button>
            {allCategories.map((c) => {
              const style = CATEGORY_STYLES[c] || DEFAULT_STYLE;
              const active = filter.category === c;
              const count = resources.filter((r) => r.category === c).length;
              return (
                <button
                  key={c}
                  onClick={() => setFilter({ ...filter, category: active ? "" : c })}
                  className="font-mono-label px-3 py-1.5 border-2 whitespace-nowrap transition-colors"
                  style={
                    active
                      ? { backgroundColor: style.bg, color: style.fg, borderColor: style.bg }
                      : { borderColor: "#E0D8CE" }
                  }
                  data-testid={`ressources-cat-${c}`}
                >
                  {c} <span className="opacity-60">· {count}</span>
                </button>
              );
            })}
            <span className="text-[#E0D8CE] mx-1">|</span>
            {["", "fr", "en", "creole"].map((l) => (
              <button
                key={l || "all"}
                onClick={() => setFilter({ ...filter, language: l })}
                className={`font-mono-label px-3 py-1.5 border-2 whitespace-nowrap ${filter.language === l ? "bg-[#4A7A3D] text-[#FAF8F3] border-[#4A7A3D]" : "border-[#E0D8CE] hover:border-[#1A1A18]"}`}
                data-testid={`ressources-lang-${l || "all"}`}
              >
                {l === "" ? "Toutes langues" : LANG_LABEL[l]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="border-b border-[#E0D8CE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {loading ? (
            <p className="font-mono-label">CHARGEMENT…</p>
          ) : visibleCategories.length === 0 ? (
            <p className="font-quote text-2xl text-[#4B4B48] py-12 text-center">Aucune ressource ne correspond à ces filtres.</p>
          ) : (
            <div className="space-y-20">
              {visibleCategories.map((cat) => {
                const style = CATEGORY_STYLES[cat] || DEFAULT_STYLE;
                return (
                  <section key={cat} data-testid={`ressources-cat-${cat}`}>
                    {/* Strong category header */}
                    <header
                      className="flex flex-wrap justify-between items-baseline gap-3 mb-8 px-5 py-4"
                      style={{ backgroundColor: style.bg, color: style.fg }}
                    >
                      <h2 className="font-display text-3xl md:text-5xl leading-tight uppercase">{cat}</h2>
                      <span className="font-mono-label" style={{ color: style.accent }}>
                        {byCategory[cat].length} ressource{byCategory[cat].length > 1 ? "s" : ""}
                      </span>
                    </header>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-2 border-[#1A1A18]">
                      {byCategory[cat].map((r, i) => (
                        <a
                          key={r.id}
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-6 hover:bg-[#1A1A18] hover:text-[#FAF8F3] transition-colors flex flex-col relative border-r border-b border-[#E0D8CE] last:border-r-0"
                          data-testid={`ressource-${r.id}`}
                        >
                          {/* Big number watermark */}
                          <div
                            className="absolute top-3 right-4 font-display text-5xl leading-none opacity-10 group-hover:opacity-30 transition-opacity"
                            style={{ color: style.accent }}
                          >
                            {String(i + 1).padStart(2, "0")}
                          </div>
                          <div className="flex justify-between items-start gap-2 mb-4 relative">
                            {r.source_name && (
                              <span
                                className="font-mono-label px-2 py-1"
                                style={{ backgroundColor: style.accent, color: style.bg === "#FAF8F3" ? "#FAF8F3" : style.bg }}
                              >
                                {r.source_name}
                              </span>
                            )}
                            <span className="font-mono-label text-[#4B4B48] group-hover:text-[#FAF8F3]/60">
                              {LANG_LABEL[r.language] || r.language?.toUpperCase()}
                            </span>
                          </div>
                          <h3 className="font-display text-xl md:text-2xl leading-[1.1] relative mb-3">
                            {r.title}
                          </h3>
                          {r.description && (
                            <p className="text-sm leading-relaxed flex-1 text-[#4B4B48] group-hover:text-[#FAF8F3]/80 relative">
                              {r.description}
                            </p>
                          )}
                          <div className="font-mono-label mt-5 flex items-center gap-1.5 relative" style={{ color: style.accent }}>
                            VISITER LA SOURCE <ExternalLink size={12} />
                          </div>
                        </a>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#C4944A] text-[#1A1A18]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="font-mono-label mb-3">Une ressource manque ?</div>
          <h2 className="font-display text-4xl md:text-5xl leading-tight">Suggère-la-nous.</h2>
          <p className="mt-4 max-w-md mx-auto">Une étude, un média, une organisation, une thèse mauricienne — partage-nous le lien et nous l'ajoutons.</p>
          <a
            href="/contact"
            className="inline-block mt-8 bg-[#1A1A18] text-[#C4944A] px-8 py-4 font-mono-label hover:bg-[#FAF8F3] hover:text-[#1A1A18]"
            data-testid="ressources-contact-cta"
          >
            Suggérer une ressource →
          </a>
        </div>
      </section>
    </div>
  );
}
